import {get, post} from '@/services/infrastructure/api.service'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SETTINGS_TAB_SWITCHED, ORG_SWITCHED} from '@/types/events'
import {format} from 'date-fns'
import {useSingletonEmitter} from '@/services/infrastructure/emitter'
/* TODO: ///
      This service seems like a violation of both SRP and OC principles.
      It would have to grow indefinitely as we add more features.
      We should look at how we can allow settings from different features to be registered,
      instead of this service having to know everything.
 */
export type SettingName = 'DEFAULT_INCOMING_DOCUMENT_STATUS' |
    'VAT_LIABLE' |
    'VAT_LIABLE_SINCE' |
    'VAT_ANNUALLY' |
    'PROJECT_REQUIRED' |
    'DEPARTMENT_REQUIRED' |
    'DEFAULT_OUTGOING_PAYMENT_ACCOUNT' |
    'DEFAULT_INCOMING_PAYMENT_ACCOUNT' |
    'DEFAULT_TAX_DEDUCTION_ACCOUNT' |
    'AUTO_SAVE_DRAFT' |
    'FRONTEND_NAVBAR_FAVORITES' |
    'DEFAULT_INVOICE_DUE_DATE' |
    'DEFAULT_INVOICE_REFERENCE' |
    'DESCRIPTION_REQUIRED' |
    'CREATE_TRANSACTION_LINE_FIELDS' |
    'KRAVIA_ENABLED' |
    'KRAVIA_FIRST_REMINDER_TYPE' |
    'KRAVIA_FIRST_REMINDER_WHEN' |
    'KRAVIA_COLLECTION_MINIMUM_AMOUNT' |
    'KRAVIA_INVITED_EMAILS' |
    'KRAVIA_BANK_LEDGER_ACCOUNT' |
    'KRAVIA_INTEREST_FEE_ACCOUNT' |
    'KRAVIA_VAT_ACCOUNT' |
    'KRAVIA_COLLECTOR_EXPENSES_ACCOUNT' |
    'KRAVIA_FEES_PAID_IN_ADVANCE_ACCOUNT' |
    'KRAVIA_COLLECTION_AGENCY_ACCOUNT' |
    'KRAVIA_BANK_ACCOUNT_NUMBER' |
    'KRAVIA_CREDITOR_ID' |
    'ONBOARDING_ORGANIZATION_COMPLETE' |
    'REGISTERED_EHF_RECIPIENT' |
    'INVOICE_INCLUDE_PHONE' |
    'INVOICE_SUPPLIER_PHONE' |
    'INVOICE_INCLUDE_EMAIL' |
    'INVOICE_SUPPLIER_EMAIL' |
    'FEATURE_TOGGLE_ASSIGNMENT_LIST'

export type SettingType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null' | 'date'

export type Setting<T> = {
    readonly name: SettingName
    readonly type: SettingType
    value?: T
    readonly userId?: string
    readonly min?: T
    readonly max?: T
    readonly default?: T
}

const list = async (): Promise<Setting<any>[]> => {
    return await get('/setting') ?? []
}

const settings = ref<Setting<any>[]>([])

const fetch = async () => {
    settings.value = await list()
}

const getSetting = async (name: SettingName) => {
    return await get('/setting/' + name)
}

export const refreshSetting = async (name: SettingName) => {
    const setting = settings.value.find(s => s.name === name)
    if (setting) {
        const updatedSetting = await getSetting(name)
        Object.assign(setting, updatedSetting)
    } else {
        console.warn(`Setting with name ${name} not found in cache, fetching from server`)
        await fetch()
    }
}

const clear = () => {
    settings.value = []
}

export const setSetting = async <T>(name: SettingName, value: T, userOverride: boolean = false): Promise<Setting<T>> => {
    let formattedValue: any = value
    if (value instanceof Date) {
        formattedValue = format(value, 'yyyy-MM-dd')
    }

    const result = await post('/setting', {name, value: formattedValue, userOverride})
    await fetch()
    return result
}

export const useSetting = <T>(name: SettingName) => {
    const {onEvents, cleanUp} = useSingletonEmitter('setting')
    onMounted(async () => {
        if (settings.value.length === 0) {
            await fetch()
        }
        onEvents([ORG_SELECTED, ORG_SWITCHED, ORG_SETTINGS_TAB_SWITCHED], async () => {
            clear()
            await fetch()
        })
        onEvents([NO_ORG_SELECTED, LOGOUT], clear)
    })

    onUnmounted(() => {
        cleanUp()
    })

    // TODO any way we can avoid setting value like:
    // setting.value.value = newValue
    // to something like
    // setting.value = newValue
    // only reading needs to be reactive
    const setting = computed(() => {
        const setting = settings.value.find(setting => setting.name === name) as Setting<T>
        return {
            ...setting,
            get value(): T | undefined {
                return setting?.value
            },
            set value(value: T) {
                setSetting(setting.name, value)
            }
        }
    })

    return {
        setting
    }
}